import { DANCE_STYLE } from "@dw/models/danceClass";
import { IGeoPoint, IMenuItem } from "@dw/models/shared";

export const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again later.';

/**
 * Shown when a request could not be authenticated because the token refresh never got an answer -
 * offline, a blip, a machine whose network has not come back yet. Deliberately NOT a sign-out: the
 * refresh token is still good, so the next attempt succeeds and the session carries on.
 */
export const CONNECTION_ERROR_MESSAGE = 'We could not reach Dance World. Check your connection and try again.';

export const QUERY_CLIENT_STALE_TIME = 1000 * 60 * 60; // 60 Minutes

// Defaults to Spark Arena, Auckland, NZ
export const DEFAULT_MAP_CENTER: IGeoPoint = {
  latitude: -36.8477629,
  longitude: 174.7765824,
};

export const ROUTES = {
  Root: '/',
  Login: '/login',

  Dashboard: '/dashboard',
  ManageDanceSchool: '/dashboard/dance-school',
  ManageLocation: '/dashboard/locations/:locationId',
  ManageDanceClass: '/dashboard/classes/:danceClassId',
  ManageMyProfile: '/dashboard/my-profile',


  CreateStudent: '/dashboard/students/create-new',
  ManageStudent: '/dashboard/students/:studentId',

  ViewDancerAccount: '/dashboard/accounts/:accountId',

  ViewCommunicationDetails: '/dashboard/communication/:messageId',

  RegisterTrialClass: '/dashboard/trials/register-new',

  BulkInvoices: '/dashboard/bulk-invoices',

  ManageExam: '/dashboard/exams/:examId',
  ManageExamClass: '/dashboard/exams/:examId/classes/:examClassId',

  PublicInvoiceView: '/invoice/:invoiceId',

  ExaminerHome: '/examiner/:token',
  ExaminerClass: '/examiner/:token/classes/:examClassId',
};

export enum MANAGE_DANCE_SCHOOL_LOCATION_TAB {
  STUDIOS = 'studios',
  SCHEDULE = 'schedule',
  CLASSES = 'classes',
  STUDENTS = 'location-students'
}

export enum MANAGE_DANCE_SCHOOL_STUDENTS_TAB {
  STUDENT_LIST = 'student-list',
  ENROLMENTS = 'enrolments',
  TRIALS = 'trials',
  ACCOUNTS = 'accounts',
  INVOICES = 'invoices'
}

export enum MANAGE_DANCE_SCHOOL_TAB {
  LOCATIONS = 'locations',
  STUDENTS = 'students',
  SETTINGS = 'settings',
  STAFF = 'staff',
  BUSINESS_METRICS = 'business-metrics',
  COMMUNICATION = 'communication',
  EXAMS = 'exams',
}

/**
 * The Settings sub-tabs, in the order a school actually sets itself up: who you are, what your
 * dancers see, then enrolment, money, email and the outside world.
 *
 * <b>The values are URLs and are deliberately frozen.</b> They are read from `?subTab=` and written
 * by a dozen deep-links across Students, Trials, Business Metrics, Communications and the Xero OAuth
 * return, and they sit in bookmarks. So `PUBLIC_INFO` still spells `public-info` even though the tab
 * is now called "Your School" — the label is what the owner reads, the value is an address.
 */
export enum MANAGE_DANCE_SCHOOL_SETTINGS_TAB {
  PUBLIC_INFO = 'public-info',
  DANCER_APP = 'dancer-app',
  ENROLMENT_SETTINGS = 'enrolment-settings',
  INVOICE_SETTINGS = 'invoice-settings',
  EMAIL_SETTINGS = 'email-settings',
  INTEGRATIONS = 'integrations',
}

/**
 * ManageDanceClassPage's tabs: the class itself, and the files a studio has shared against it.
 * A fixed set of options, so an enum — never a string-literal union.
 */
export enum MANAGE_DANCE_CLASS_TAB {
  DETAILS = 'details',
  FILES = 'files'
}

export enum MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB {
  MESSAGES = 'messages',
  TEACHER_NOTES = 'teacher-notes',
  NOTICE_BOARD = 'notice-board',
}

/**
 * ManageExamPage's tabs (Part 2): setup (Classes) and exam-day operations (Examiner Scoring) are both
 * first-class instead of one buried below the other. Values prefixed like MANAGE_EXAM_CLASS_TAB's.
 */
export enum MANAGE_EXAM_TAB {
  CLASSES = 'exam-classes',
  EXAMINER_SCORING = 'examiner-scoring',
}

/**
 * The Chip sub-tabs inside a single exam class. A fixed set of options, so an enum — never a
 * string-literal union. Values are prefixed because `?subTab=students` would collide with
 * MANAGE_DANCE_SCHOOL_STUDENTS_TAB when both appear in the same URL.
 */
export enum MANAGE_EXAM_CLASS_TAB {
  STUDENTS = 'exam-class-students',
  EXERCISES = 'exam-class-exercises',
  SCORES = 'exam-class-scores',
}

export const APP_TABS_LABELS: Record<MANAGE_DANCE_SCHOOL_LOCATION_TAB | MANAGE_DANCE_SCHOOL_TAB | MANAGE_DANCE_SCHOOL_STUDENTS_TAB | MANAGE_DANCE_SCHOOL_SETTINGS_TAB | MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB | MANAGE_EXAM_TAB | MANAGE_EXAM_CLASS_TAB | MANAGE_DANCE_CLASS_TAB, string> = {
  [MANAGE_DANCE_SCHOOL_LOCATION_TAB.STUDIOS]: 'Studios',
  [MANAGE_DANCE_SCHOOL_LOCATION_TAB.SCHEDULE]: 'Schedule',
  [MANAGE_DANCE_SCHOOL_LOCATION_TAB.CLASSES]: 'Classes',
  [MANAGE_DANCE_SCHOOL_LOCATION_TAB.STUDENTS]: 'Students',

  [MANAGE_DANCE_SCHOOL_TAB.LOCATIONS]: 'Locations',
  [MANAGE_DANCE_SCHOOL_TAB.STUDENTS]: 'Students',
  [MANAGE_DANCE_SCHOOL_TAB.SETTINGS]: 'Settings',
  [MANAGE_DANCE_SCHOOL_TAB.STAFF]: 'Staff',
  [MANAGE_DANCE_SCHOOL_TAB.BUSINESS_METRICS]: 'Business Metrics',
  [MANAGE_DANCE_SCHOOL_TAB.COMMUNICATION]: 'Communication',
  [MANAGE_DANCE_SCHOOL_TAB.EXAMS]: 'Exams',

  [MANAGE_DANCE_SCHOOL_STUDENTS_TAB.STUDENT_LIST]: 'Student List',
  [MANAGE_DANCE_SCHOOL_STUDENTS_TAB.ENROLMENTS]: 'Enrolments',
  [MANAGE_DANCE_SCHOOL_STUDENTS_TAB.TRIALS]: 'Trials',
  [MANAGE_DANCE_SCHOOL_STUDENTS_TAB.ACCOUNTS]: 'Accounts',
  [MANAGE_DANCE_SCHOOL_STUDENTS_TAB.INVOICES]: 'Invoices',

  // These sit inside a tab already called Settings, so four of them used to say "Settings" twice.
  [MANAGE_DANCE_SCHOOL_SETTINGS_TAB.PUBLIC_INFO]: 'Your School',
  [MANAGE_DANCE_SCHOOL_SETTINGS_TAB.DANCER_APP]: 'Dancer App',
  [MANAGE_DANCE_SCHOOL_SETTINGS_TAB.ENROLMENT_SETTINGS]: 'Enrolment',
  [MANAGE_DANCE_SCHOOL_SETTINGS_TAB.INVOICE_SETTINGS]: 'Invoicing',
  [MANAGE_DANCE_SCHOOL_SETTINGS_TAB.EMAIL_SETTINGS]: 'Email',
  [MANAGE_DANCE_SCHOOL_SETTINGS_TAB.INTEGRATIONS]: 'Integrations',

  [MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB.MESSAGES]: 'Messages',
  [MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB.TEACHER_NOTES]: 'Teacher Notes',
  [MANAGE_DANCE_SCHOOL_COMMUNICATION_TAB.NOTICE_BOARD]: 'Notice Board',

  [MANAGE_EXAM_TAB.CLASSES]: 'Classes',
  [MANAGE_EXAM_TAB.EXAMINER_SCORING]: 'Examiner Scoring',

  [MANAGE_EXAM_CLASS_TAB.STUDENTS]: 'Dancers',
  [MANAGE_EXAM_CLASS_TAB.EXERCISES]: 'Exercises',
  [MANAGE_EXAM_CLASS_TAB.SCORES]: 'Scores',

  [MANAGE_DANCE_CLASS_TAB.DETAILS]: 'Details',
  [MANAGE_DANCE_CLASS_TAB.FILES]: 'Files',
}

export enum DAY_OF_WEEK {
  MONDAY = 'Monday',
  TUESDAY = 'Tuesday',
  WEDNESDAY = 'Wednesday',
  THURSDAY = 'Thursday',
  FRIDAY = 'Friday',
  SATURDAY = 'Saturday',
  SUNDAY = 'Sunday',
}
export const DAY_OF_WEEK_NUMBERS: Record<DAY_OF_WEEK, number> = {
  [DAY_OF_WEEK.SUNDAY]: 0,
  [DAY_OF_WEEK.MONDAY]: 1,
  [DAY_OF_WEEK.TUESDAY]: 2,
  [DAY_OF_WEEK.WEDNESDAY]: 3,
  [DAY_OF_WEEK.THURSDAY]: 4,
  [DAY_OF_WEEK.FRIDAY]: 5,
  [DAY_OF_WEEK.SATURDAY]: 6
}

const splitByUpperCase = (input?: string): string => !input ? '' : (
  `${input.replace(/([A-Z](?=[a-z]+)|[A-Z]+(?![a-z]))/g, ' $1').slice(1)}`
);
export const DAY_OF_WEEK_MENU_OPTIONS: IMenuItem[] = Object.values(DAY_OF_WEEK).map((x) => ({
  value: x,
  text: splitByUpperCase(x)
}) as IMenuItem);
export const DANCE_STYLE_MENU_OPTIONS: IMenuItem[] = Object.values(DANCE_STYLE).map((x) => ({
  value: x,
  text: splitByUpperCase(x)
}) as IMenuItem);

export enum DATE_FORMATS {
  FullDateTime = 'D MMM YYYY hh:mma',

  DayMonthYear = 'D MMM YYYY',

  DayMonthYearWithFullDay = 'dddd, D MMM YYYY',

  DayMonth = 'D MMM',

  Day = 'D',

  DayOfWeek = 'ddd',

  Hour = 'h a',

  HourMinute = 'h:mma',

  HourMinuteSecond = 'hh:mm:ssa',

  HourMinuteSecond24Hour = 'HH:mm:ss',

  DayMonthHour = 'D MMM hh:mma',

  YearMonthDayIso = 'YYYY-MM-DD'
};

export enum THIRD_PARTY_PROVIDER {
  XERO = "Xero",
  GOOGLE = "Google",
  FACEBOOK = "Facebook",
  APPLE = "Apple"
}

export enum SCREEN_SIZE {
  XS = 0,
  SM = 1,
  MD = 2,
  LG = 3,
  XL = 4
}

export enum STORAGE_KEYS {
  AcceptInvite = 'dw_accept_invite_details',
  BusinessMetricsSettings = 'dw_use_business_metrics_settings',
  /**
   * The access + refresh token pair. Written to localStorage when the user ticked
   * "Remember this device", otherwise to sessionStorage so the session dies with the tab.
   */
  AuthSession = 'dw_auth_session',
  /**
   * The last successful `/v1/auth/initialize` response — what lets a cold load paint the dashboard
   * optimistically before the background re-initialize returns. Written to the same store as
   * `AuthSession`: it holds the user's name, school and role, so declining "Remember this device"
   * has to leave nothing behind on a shared machine.
   */
  AuthSnapshot = 'dw_auth_snapshot',
  /**
   * The last export options used per exam (P3-37). Local rather than session because "come back and
   * tweak the certificates" is a cross-session act — the SESSION_STORAGE_KEYS entry of the same name
   * is where this lived until then and is deliberately left in place.
   */
  ExamReportExportOptions = 'dw_exam_report_export_options'
}

export enum SESSION_STORAGE_KEYS {
  InvoiceFilters = 'InvoiceFilters',
  CalendarDateRangeStart = 'CalendarDateRangeStart',
  DanceClassesScheduleFilter = 'DanceClassesScheduleFilter',
  ActiveEnrolmentYear = 'ActiveEnrolmentYear',
  /** Superseded by the STORAGE_KEYS entry of the same name (P3-37). Kept — enum values are never removed. */
  ExamReportExportOptions = 'ExamReportExportOptions'
}